import React, { useState } from 'react';

import type { TransactionProps } from 'app-components/flux/transactions/transactionsTypes';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';

import StdDialogActions from 'components/Dialogs/StdDialogActions';
import SearchBox from 'components/SearchBox';
import TransactionSelectList from 'components/TransactionSelectList';
import { getLesserBilledTransactions } from 'data/transactionList/selectors';

import { useSelectTransactionPickerStyles } from './styles';

export type SeriesTransactionPickerContentProps = {
  subtitle?: string;
  selectorProps?: Record<string, unknown>;
  /** Defaults to 'Create' — both current callers (Bill Setup, Create Series) use this label. */
  primaryButtonText?: string;
  onTransactionSelected: (transaction?: TransactionProps) => void;
  onClose: () => void;
};

/**
 * Search + pick-one-transaction content shared by the Bill Setup "Missing
 * something?" drawer and the Create Series "pick an existing transaction"
 * flow — the candidate selector, search behavior, and "Add manually" tertiary
 * action are identical between the two; only the surrounding dialog/drawer
 * chrome differs per caller (see `SelectTransactionDrawer` and
 * `SelectSeriesTransactionDialog`).
 */
export default function SeriesTransactionPickerContent({
  subtitle,
  selectorProps,
  primaryButtonText = 'Create',
  onTransactionSelected,
  onClose,
}: SeriesTransactionPickerContentProps): React.JSX.Element {
  const { classes } = useSelectTransactionPickerStyles();
  const [selected, setSelected] = useState<TransactionProps | null>(null);
  const [search, setSearch] = useState<string | null>(null);

  const selectTxn = (event: React.MouseEvent<HTMLElement>) => {
    const newSelect = JSON.parse((event.currentTarget as HTMLElement).id);
    setSelected((current) => (current?.id === newSelect.id ? null : newSelect));
  };

  const handleCreate = () => {
    onTransactionSelected(selected ?? undefined);
    setSelected(null);
    setSearch(null);
  };

  const handleManualAdd = () => {
    setSelected(null);
    setSearch(null);
    onTransactionSelected();
  };

  return (
    <>
      <Box className={classes.header}>
        {subtitle ? (
          <Typography variant='paragraphRegular' color='text.secondary'>
            {subtitle}
          </Typography>
        ) : null}
        <SearchBox
          onSearch={setSearch}
          autoSearch
          autoFocus
          collapsable={false}
          initialValue={search}
          placeholder='Search transactions'
        />
      </Box>
      <Box className={classes.listArea}>
        <TransactionSelectList
          clickFunc={selectTxn}
          highlight={selected?.id}
          filter={search}
          onReset={() => setSearch(null)}
          transactionListSelector={getLesserBilledTransactions}
          selectorProps={selectorProps}
          variant='list'
          listItemSize={44}
        />
      </Box>
      <StdDialogActions
        divider
        primary={{ children: primaryButtonText, onClick: handleCreate, disabled: !selected, type: 'button' }}
        secondary={{ children: 'Cancel', onClick: onClose }}
        tertiaryNode={
          <Typography component='div' variant='paragraphRegular' color='text.secondary' className={classes.manualLine}>
            {"Couldn't find? "}
            <Button id='series-add-manually' variant='tonal' size='medium' onClick={handleManualAdd}>
              Add manually
            </Button>
          </Typography>
        }
      />
    </>
  );
}
